import { Svg } from 'components/ui'
import cn from 'classnames'
import { Fragment, h } from 'preact'
import { ForwardFn, forwardRef, useImperativeHandle } from 'preact/compat'
import { FeedbackFieldRefType, RatingDescription, RatingField as RatingFieldType } from 'scripts/type'
import { useMemo, useRef, useState } from 'preact/hooks'
import { useParentWindowSize, useTranslation } from 'scripts/hooks'

import styles from './index.scss'

type Props = {
  field: RatingFieldType
  onChange: (value: number) => void
}

const starDefaultTopInterval = 8
const starSlideUpDurationInterval = 80
const starClickAnimationDuration = 800

const RatingField: ForwardFn<Props, FeedbackFieldRefType> = ({ field, onChange }, ref) => {
  const { windowSizeSm } = useParentWindowSize()
  const [slideUp, setSlideUp] = useState(false)
  const [currentValue, setCurrentValue] = useState(-1)
  const [hoverIndex, setHoverIndex] = useState<null | number>(null)
  const [clicked, setClicked] = useState(false)
  const starsRef = useRef<HTMLDivElement>(null)
  const ratingMax = field.ratingMax || 5
  const stars = useMemo(() => Array(ratingMax).fill(''), [ratingMax])
  const { hate, dislike, neutral, like, love } = (useTranslation()('ratingDescription') || {}) as RatingDescription
  const descMap = [hate, dislike, neutral, like, love]

  useImperativeHandle(ref, () => ({
    dom: starsRef.current!,
    fadeIn: () => {
      if (starsRef.current) {
        starsRef.current.style.opacity = '1'
        setSlideUp(true)
      }
    },
  }))

  const onClick = (index: number) => () => {
    if (index === currentValue) {
      return
    }
    const isFirstClick = currentValue === -1
    setClicked(true)
    setCurrentValue(index)
    if (!isFirstClick) {
      onChange?.(index + 1)
    }
    setTimeout(() => {
      isFirstClick && onChange?.(index + 1)
      setClicked(false)
    }, starClickAnimationDuration)
  }

  const getStyles = (idx: number) => {
    const defaultTop = (idx + 1) * starDefaultTopInterval * 0.8
    const transitionDuration = (idx + 1) * starSlideUpDurationInterval + 'ms'
    return {
      defaultTop,
      transitionDuration,
    }
  }

  return (
    <Fragment>
      <div className={styles.label}>{field.label}</div>
      <div className={cn(styles.stars)} ref={starsRef}>
        {stars.map((x, i) => {
          const { defaultTop, transitionDuration } = getStyles(i)
          const isHover = currentValue >= i || (typeof hoverIndex === 'number' && hoverIndex >= i)
          const showDescText = typeof hoverIndex === 'number' ? hoverIndex === i : currentValue === i
          const showClickedAnimation = clicked && currentValue >= i
          return (
            <div
              key={i + 'x'}
              className={cn(styles.starWrap, windowSizeSm && styles.starWrapSm)}
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <div
                className={cn(
                  styles.starBox,
                  windowSizeSm && styles.starBoxSm,
                  slideUp && styles.slideUp,
                  isHover && styles.hover,
                  showClickedAnimation && styles.clicked
                )}
                style={{ top: defaultTop, transitionDuration, animationDuration: starClickAnimationDuration + 'ms' }}
                onClick={onClick(i)}
              >
                <Svg.FeedbackStar className={cn(styles.starSvg)} />
              </div>
              {showDescText && <span className={styles.selectedDesc}>{descMap[i]}</span>}
            </div>
          )
        })}
      </div>
    </Fragment>
  )
}

export default forwardRef(RatingField)
